import {
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfDay,
  format,
  isWithinInterval,
  parse,
  startOfDay,
} from "date-fns";
import type { DateRange } from "react-day-picker";
import { HandleError } from "@/api/axiosClient";
import {
  PerkPointReportsApi,
  PerkPointTransactionApi,
  type PointsExpiryReportItem,
  type TransactionReportItem,
} from "@/api/giftcard.api";
import { getFormattedDateTime } from "@/utils";
import { apiErrorMessage } from "@/api/perkpoint/client";
import {
  PerkPointAuditApi,
  auditExplorerUrl,
  auditRangeParams,
  type AuditEntry,
} from "@/api/perkpointAudit.api";
import { getManagerSession } from "../programs/program-scope";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type CsvTable = {
  headers: string[];
  rows: Array<Array<string | number>>;
};

const PAGE_SIZE = 200;
const MAX_PAGES = 50;
const EMPTY_API_DATE = "0001-01-01T00:00:00";

function escapeCsvField(value: string | number) {
  const text = String(value ?? "");
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function downloadCsvFile(filename: string, table: CsvTable) {
  const lines = [table.headers, ...table.rows].map((row) =>
    row.map(escapeCsvField).join(","),
  );
  const blob = new Blob([`\uFEFF${lines.join("\n")}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function reportFileName(slug: string, range: DateRange) {
  const from = range.from ? format(range.from, "yyyy-MM-dd") : "start";
  const to = range.to ? format(range.to, "yyyy-MM-dd") : from;
  return `${slug}_${from}_to_${to}.csv`;
}

function isInRange(value: string | null | undefined, range: DateRange) {
  if (!range.from) return true;
  if (!value || value === EMPTY_API_DATE) return false;

  const isoDate = new Date(value);
  const parsed = Number.isNaN(isoDate.getTime())
    ? parse(value, "MMM d, yyyy h:mm a", new Date())
    : isoDate;
  if (Number.isNaN(parsed.getTime())) {
    const dateOnly = parse(value, "MMM d, yyyy", new Date());
    if (Number.isNaN(dateOnly.getTime())) return false;
    return isWithinInterval(dateOnly, {
      start: startOfDay(range.from),
      end: endOfDay(range.to ?? range.from),
    });
  }

  return isWithinInterval(parsed, {
    start: startOfDay(range.from),
    end: endOfDay(range.to ?? range.from),
  });
}

function displayDate(value: string | null | undefined) {
  if (!value || value === EMPTY_API_DATE) return "";
  return getFormattedDateTime(value) || value;
}

async function fetchAllPages<T>(
  request: (
    pageNumber: number,
    pageSize: number,
  ) => Promise<{ rows: T[]; total: number; errorMsg?: string }>,
) {
  const first = await request(1, PAGE_SIZE);
  if (first.errorMsg?.trim() && first.rows.length === 0) {
    throw new Error(first.errorMsg);
  }

  const rows = [...first.rows];
  const total = Number(first.total) || rows.length;
  let page = 2;

  while (rows.length < total && page <= MAX_PAGES) {
    const next = await request(page, PAGE_SIZE);
    if (next.rows.length === 0) break;
    rows.push(...next.rows);
    page += 1;
  }

  return rows;
}

export async function downloadCustomerActivityReport(
  range: DateRange,
): Promise<CsvTable> {
  const transactions = await fetchAllPages<TransactionReportItem>(
    async (pageNumber, pageSize) => {
      const response = await PerkPointTransactionApi.getAllTransactions({
        p_PageNumber: pageNumber,
        p_PageSize: pageSize,
      });
      const rows = Array.isArray(response.allTransactions)
        ? response.allTransactions
        : [];
      return {
        rows,
        total: response.total_records ?? rows.length,
        errorMsg: response.error_msg,
      };
    },
  );

  const filtered = transactions.filter((row) =>
    isInRange(row.transactedDate, range),
  );

  return {
    headers: [
      "Mobile No",
      "Invoice No",
      "Invoice Amount",
      "Transaction Type",
      "Transacted Date",
      "Counter ID",
    ],
    rows: filtered.map((row) => [
      row.mobileNo || "",
      row.invoiceNo || "",
      row.invoiceAmt ?? "",
      row.transactionType || "",
      displayDate(row.transactedDate),
      row.counterId || "",
    ]),
  };
}

export async function downloadPointExpiryReport(
  range: DateRange,
): Promise<CsvTable> {
  const expiries = await fetchAllPages<PointsExpiryReportItem>(
    async (pageNumber, pageSize) => {
      const response = await PerkPointReportsApi.getPointsExpiryReport({
        p_PageNumber: pageNumber,
        p_PageSize: pageSize,
      });
      const rows = Array.isArray(response.expiryResponse)
        ? response.expiryResponse
        : [];
      return {
        rows,
        total: response.total_records ?? rows.length,
        errorMsg: response.error_msg,
      };
    },
  );

  const filtered = expiries.filter((row) => isInRange(row.expiredDate, range));

  return {
    headers: [
      "Mobile No",
      "Name",
      "Outlet Name",
      "Expired Points",
      "Expired Date",
    ],
    rows: filtered.map((row) => [
      row.mobileNo || "",
      row.name || "",
      row.outletName || "",
      row.expiredPoints ?? "",
      displayDate(row.expiredDate),
    ]),
  };
}

/**
 * Every audit entry in the range, newest first. A program manager gets their
 * program; anyone else gets all programs.
 */
export async function downloadAuditHistoryReport(
  range: DateRange,
): Promise<CsvTable> {
  const session = getManagerSession();
  const programId =
    session?.programId && UUID_PATTERN.test(session.programId)
      ? session.programId
      : null;
  const entries: AuditEntry[] = [];
  try {
    let total = Infinity;
    for (let page = 1; entries.length < total && page <= MAX_PAGES; page += 1) {
      const result = await PerkPointAuditApi.list({
        programId,
        ...auditRangeParams(range),
        page,
        page_size: 100,
        facets: false,
      });
      total = result.total;
      entries.push(...result.items);
      if (result.items.length === 0) break;
    }
  } catch (error) {
    throw new Error(apiErrorMessage(error, "Could not load the audit history."));
  }

  return {
    headers: [
      "Date",
      "Time",
      "Program",
      "User",
      "User Email",
      "Event",
      "Object Type",
      "Object ID",
      "Object Name",
      "Details",
      "Reason",
      "Action",
      "IP Address",
      "Entry #",
      "Hash",
      "Blockchain Tx",
      "Blockchain Explorer",
    ],
    rows: entries.map((entry) => {
      const when = new Date(entry.created_at);
      return [
        format(when, "yyyy-MM-dd"),
        format(when, "HH:mm:ss"),
        entry.program.name,
        entry.user.name,
        entry.user.email ?? "",
        entry.event_label,
        entry.object_label,
        entry.object_id,
        entry.object_name ?? "",
        entry.details,
        entry.reason ?? "",
        entry.action,
        entry.ip_address ?? "",
        entry.seq,
        entry.hash,
        entry.tx_hash ?? "",
        auditExplorerUrl(entry) ?? "",
      ];
    }),
  };
}

const POINT_VALUE = 0.25;

export type CatalogReportId =
  | "points-liability"
  | "points-by-source"
  | "breakage-forecast"
  | "redemption"
  | "ledger-detail"
  | "program-performance"
  | "member-activity"
  | "enrolment"
  | "campaign-performance"
  | "segment-snapshot"
  | "segment-members"
  | "tier-movement"
  | "rule-cost";

function monthsInRange(range: DateRange) {
  if (!range.from) return [];
  return eachMonthOfInterval({
    start: startOfDay(range.from),
    end: startOfDay(range.to ?? range.from),
  });
}

function daysInRange(range: DateRange, limit = 8) {
  if (!range.from) return [];
  const days = eachDayOfInterval({
    start: startOfDay(range.from),
    end: startOfDay(range.to ?? range.from),
  });
  if (days.length <= limit) return days;
  const step = (days.length - 1) / (limit - 1);
  return Array.from({ length: limit }, (_, index) => days[Math.round(index * step)]);
}

function money(points: number) {
  return Math.round(points * POINT_VALUE);
}

function monthLabel(date: Date) {
  return format(date, "MMM yyyy");
}

function dayLabel(date: Date) {
  return format(date, "MMM d, yyyy");
}

export async function downloadCatalogReport(
  id: CatalogReportId,
  range: DateRange,
): Promise<CsvTable> {
  const months = monthsInRange(range);
  const days = daysInRange(range);

  if (id === "points-liability") {
    return {
      headers: [
        "Month",
        "Opening balance (points)",
        "Opening balance (money)",
        "Issued (points)",
        "Issued (money)",
        "Redeemed (points)",
        "Redeemed (money)",
        "Expired (points)",
        "Expired (money)",
        "Reversed (points)",
        "Reversed (money)",
        "Adjusted (points)",
        "Adjusted (money)",
        "Closing balance (points)",
        "Closing balance (money)",
        "Pending (points)",
        "Pending (money)",
        "Available (points)",
        "Available (money)",
      ],
      rows: months.map((month, index) => {
        const opening = 420000 + index * 18000;
        const issued = 86000 + index * 4000;
        const redeemed = 31000 + index * 1500;
        const expired = 6400;
        const reversed = 1200;
        const adjusted = 800;
        const closing = opening + issued - redeemed - expired + reversed + adjusted;
        const pending = 14000 + index * 500;
        const available = closing - pending;
        return [
          monthLabel(month),
          opening,
          money(opening),
          issued,
          money(issued),
          redeemed,
          money(redeemed),
          expired,
          money(expired),
          reversed,
          money(reversed),
          adjusted,
          money(adjusted),
          closing,
          money(closing),
          pending,
          money(pending),
          available,
          money(available),
        ];
      }),
    };
  }

  if (id === "points-by-source") {
    const sources = [
      ["Rule", 42000, 10500],
      ["Campaign", 18000, 4500],
      ["Tier benefit", 9600, 2400],
      ["Invite code", 5400, 1350],
      ["Admin adjustment", 2200, 550],
    ] as const;
    return {
      headers: ["Month", "Source", "Points", "Cost"],
      rows: months.flatMap((month) =>
        sources.map(([source, points, cost]) => [
          monthLabel(month),
          source,
          points,
          cost,
        ]),
      ),
    };
  }

  if (id === "breakage-forecast") {
    return {
      headers: [
        "Month",
        "Expiring in 30 days",
        "Expiring in 60 days",
        "Expiring in 90 days",
        "Forecast expiry",
        "Actual expiry",
      ],
      rows: months.map((month, index) => [
        monthLabel(month),
        8200 + index * 300,
        14600 + index * 400,
        19800 + index * 500,
        7800 + index * 200,
        6400 + index * 150,
      ]),
    };
  }

  if (id === "redemption") {
    const spentOn = ["Voucher", "Booking discount", "Gift card", "Partner offer"];
    return {
      headers: ["Date", "Points burned", "Money value", "Spent on"],
      rows: days.map((day, index) => {
        const points = 1800 + index * 220;
        return [dayLabel(day), points, money(points), spentOn[index % spentOn.length]];
      }),
    };
  }

  if (id === "ledger-detail") {
    const types = ["Issued", "Redeemed", "Expired", "Reversed", "Adjusted"];
    const sources = ["Rule", "Campaign", "Tier benefit", "Invite code", "Admin adjustment"];
    return {
      headers: [
        "Entry date",
        "Member ID",
        "Type",
        "Points",
        "Source",
        "Booking reference",
      ],
      rows: days.flatMap((day, dayIndex) =>
        types.map((type, typeIndex) => [
          dayLabel(day),
          `MEM-${String(dayIndex * 5 + typeIndex + 1).padStart(3, "0")}`,
          type,
          type === "Redeemed" || type === "Expired" ? -(400 + typeIndex * 80) : 400 + typeIndex * 80,
          sources[typeIndex],
          `BK-${format(day, "yyyyMMdd")}-${typeIndex + 1}`,
        ]),
      ),
    };
  }

  if (id === "program-performance") {
    return {
      headers: [
        "Month",
        "Members",
        "New members",
        "Active members",
        "Repeat rate",
        "Bookings",
        "Booking value",
        "Points issued",
        "Points redeemed",
        "Points liability",
        "Bronze",
        "Silver",
        "Gold",
        "Platinum",
      ],
      rows: months.map((month, index) => [
        monthLabel(month),
        15420 + index * 180,
        640 + index * 20,
        12840 + index * 90,
        "80.7%",
        4200 + index * 80,
        1860000 + index * 40000,
        86000 + index * 2000,
        31000 + index * 800,
        468000 + index * 12000,
        6820,
        4840,
        2960,
        800,
      ]),
    };
  }

  if (id === "member-activity") {
    const tiers = ["Bronze", "Silver", "Gold", "Platinum"];
    const segments = ["New", "Active", "At risk", "High value"];
    return {
      headers: [
        "Member ID",
        "Tier",
        "Balance",
        "Recency score",
        "Frequency score",
        "Monetary score",
        "Segment",
        "Lifetime spend",
        "Last booking",
      ],
      rows: days.map((day, index) => [
        `MEM-${String(index + 1).padStart(3, "0")}`,
        tiers[index % tiers.length],
        980 + index * 420,
        (index % 5) + 1,
        ((index + 1) % 5) + 1,
        ((index + 2) % 5) + 1,
        segments[index % segments.length],
        4200 + index * 900,
        dayLabel(day),
      ]),
    };
  }

  if (id === "enrolment") {
    const sources = [
      ["Organic", "", 0],
      ["Campaign", "WEEKEND", 42],
      ["Invite code", "WELCOME10", 28],
      ["Import", "", 0],
    ] as const;
    return {
      headers: ["Date", "Source", "New members", "Invite code", "Invite code uses"],
      rows: days.flatMap((day, dayIndex) =>
        sources.map(([source, code, uses], sourceIndex) => [
          dayLabel(day),
          source,
          12 + dayIndex + sourceIndex * 3,
          code,
          code ? uses + dayIndex : 0,
        ]),
      ),
    };
  }

  if (id === "campaign-performance") {
    const campaigns = ["Weekend bonus", "Refer a friend"];
    const channels = ["Email", "SMS", "WhatsApp"];
    const detail = months.flatMap((month) =>
      campaigns.flatMap((campaign, campaignIndex) =>
        channels.map((channel, channelIndex) => {
          const sent = 4200 - channelIndex * 400 - campaignIndex * 200;
          const converted = 180 - channelIndex * 20;
          const pointsCost = 6400 + channelIndex * 400;
          return [
            monthLabel(month),
            campaign,
            channel,
            sent,
            sent - 180,
            sent - 640,
            Math.round(sent * 0.22),
            converted,
            "12%",
            86000 + channelIndex * 4000,
            pointsCost,
            Math.round(money(pointsCost) / converted),
          ];
        }),
      ),
    );
    const rollup = months.map((month) => [
      monthLabel(month),
      "All campaigns",
      "All channels",
      10800,
      10240,
      8600,
      2400,
      420,
      "11%",
      246000,
      18600,
      Math.round(money(18600) / 420),
    ]);
    return {
      headers: [
        "Month",
        "Campaign",
        "Channel",
        "Sent",
        "Delivered",
        "Read",
        "Clicked",
        "Converted",
        "Lift vs control",
        "Booking value",
        "Points cost",
        "Cost per conversion",
      ],
      rows: [...detail, ...rollup],
    };
  }

  if (id === "segment-snapshot") {
    const segments = [
      ["New", "Lifecycle", 1840, 420, 90],
      ["Active", "Lifecycle", 9620, 510, 140],
      ["At risk", "Lifecycle", 1280, 160, 210],
      ["High value", "Custom", 2840, 90, 40],
    ] as const;
    return {
      headers: ["Month", "Segment", "Kind", "Members", "Joined", "Left"],
      rows: months.flatMap((month) =>
        segments.map(([name, kind, members, joined, left]) => [
          monthLabel(month),
          name,
          kind,
          members,
          joined,
          left,
        ]),
      ),
    };
  }

  if (id === "segment-members") {
    const segments = ["New", "Active", "At risk", "High value"];
    return {
      headers: ["As of", "Segment", "Member ID"],
      rows: days.flatMap((day, dayIndex) =>
        segments.map((segment, segmentIndex) => [
          dayLabel(day),
          segment,
          `MEM-${String(dayIndex * 4 + segmentIndex + 1).padStart(3, "0")}`,
        ]),
      ),
    };
  }

  if (id === "tier-movement") {
    const tiers = ["Bronze", "Silver", "Gold", "Platinum"];
    return {
      headers: [
        "Month",
        "Tier",
        "Upgrades",
        "Downgrades",
        "Manual changes",
        "Close to next tier",
        "Downgrades coming up",
      ],
      rows: months.flatMap((month) =>
        tiers.map((tier, index) => [
          monthLabel(month),
          tier,
          40 - index * 8,
          12 + index * 3,
          4,
          220 - index * 30,
          18 + index * 4,
        ]),
      ),
    };
  }

  return {
    headers: [
      "Month",
      "Rule",
      "Points",
      "Money",
      "Members affected",
      "Budget used",
    ],
    rows: months.flatMap((month) =>
      [
        ["Fixed points on each order", 42000, 10500, 6240, "68%"],
        ["Share of the invoice", 18600, 4650, 2110, "41%"],
        ["Checkout redemption", 12400, 3100, 1860, "22%"],
      ].map(([rule, points, cost, members, budget]) => [
        monthLabel(month),
        rule,
        points,
        cost,
        members,
        budget,
      ]),
    ),
  };
}

export function getReportErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message && !("response" in error)) {
    return error.message;
  }
  return HandleError(error) || fallback;
}
